<template>
  <div class="column is-12">
    <h3 class="title-section">Dispositivos activos</h3>

    <div v-if="loading" class="devices-loading">
      <span class="icon"><i class="fas fa-spinner fa-spin"></i></span>
      <span>Cargando sesiones...</span>
    </div>

    <div v-else-if="sessions.length === 0" class="devices-empty">
      No hay sesiones activas registradas.
    </div>

    <div v-else>
      <div
        v-for="session in sessions"
        :key="session.id"
        class="device-card"
        :class="{ 'device-card--current': session.isCurrent }"
      >
        <div class="device-card__icon">
          <i :class="deviceIcon(session.userAgent)"></i>
        </div>
        <div class="device-card__info">
          <div class="device-card__name">
            {{ parseUserAgent(session.userAgent) }}
            <span v-if="session.isCurrent" class="tag-current">Sesión actual</span>
          </div>
          <div class="device-card__meta">
            <span><i class="fas fa-map-marker-alt"></i> {{ session.ip || 'IP desconocida' }}</span>
            <span><i class="fas fa-clock"></i> {{ formatLastAccess(session.lastAccess) }}</span>
          </div>
        </div>
        <div class="device-card__action">
          <button
            v-if="!session.isCurrent"
            class="btn-revoke"
            @click="confirmRevoke(session)"
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>

    <modal-component
      v-if="showConfirmModal"
      :active="showConfirmModal"
      titleModal="Cerrar sesión en otro dispositivo"
      :optionalText="`¿Deseas cerrar la sesión en ${selectedSession ? parseUserAgent(selectedSession.userAgent) : 'este dispositivo'}?`"
      :isConfirm="true"
      textSave="Cerrar sesión"
      :loader="revoking"
      @hdlOnClick="doRevoke"
      @cancel="cancelRevoke"
      @accept="doRevoke"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import mixins from '@/mixins/formats'

export default {
  name: 'ActiveDevices',
  mixins: [mixins],
  components: {
    ModalComponent: () => import('@/components/ui/ModalComponent.vue')
  },
  data () {
    return {
      sessions: [],
      loading: false,
      showConfirmModal: false,
      selectedSession: null,
      revoking: false
    }
  },
  methods: {
    ...mapActions(['getActiveSessions', 'revokeSession']),

    async loadSessions () {
      this.loading = true
      try {
        this.sessions = await this.getActiveSessions() || []
      } finally {
        this.loading = false
      }
    },

    parseUserAgent (ua) {
      if (!ua) return 'Dispositivo desconocido'

      let browser = 'Navegador desconocido'
      if (/Edg\//.test(ua)) browser = 'Edge'
      else if (/OPR\/|Opera/.test(ua)) browser = 'Opera'
      else if (/Chrome\//.test(ua)) browser = 'Chrome'
      else if (/Firefox\//.test(ua)) browser = 'Firefox'
      else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = 'Safari'

      let os = ''
      if (/iPhone/.test(ua)) os = 'iPhone'
      else if (/iPad/.test(ua)) os = 'iPad'
      else if (/Android/.test(ua)) os = 'Android'
      else if (/Windows NT 10/.test(ua)) os = 'Windows 10'
      else if (/Windows NT 11/.test(ua)) os = 'Windows 11'
      else if (/Windows/.test(ua)) os = 'Windows'
      else if (/Mac OS X/.test(ua)) os = 'macOS'
      else if (/Linux/.test(ua)) os = 'Linux'

      return os ? `${browser} · ${os}` : browser
    },

    deviceIcon (ua) {
      if (!ua) return 'fas fa-desktop'
      if (/iPhone|iPad|Android/.test(ua)) return 'fas fa-mobile-alt'
      return 'fas fa-desktop'
    },

    formatLastAccess (epoch) {
      if (!epoch) return '-'
      return this.humanDate(epoch, 'DD/MM/YYYY HH:mm', true, 'es')
    },

    confirmRevoke (session) {
      this.selectedSession = session
      this.showConfirmModal = true
    },

    cancelRevoke () {
      this.showConfirmModal = false
      this.selectedSession = null
    },

    async doRevoke () {
      if (!this.selectedSession) return
      this.revoking = true
      try {
        await this.revokeSession({ sessionId: this.selectedSession.id })
        this.sessions = this.sessions.filter(s => s.id !== this.selectedSession.id)
        this.showConfirmModal = false
        this.selectedSession = null
      } finally {
        this.revoking = false
      }
    }
  },
  mounted () {
    this.loadSessions()
  }
}
</script>

<style lang="scss" scoped>
.devices-loading,
.devices-empty {
  font-size: 13px;
  color: #7a7a7a;
  padding: 1rem 0.5rem;
}

.device-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.75rem;
  border: 1px solid #EBECED;
  border-radius: 0.25rem;
  padding: 1rem 1.5rem;
  color: #484c53;

  &--current {
    border-color: #00ace0;
    background-color: #f0fbff;
  }

  &__icon {
    font-size: 1.5rem;
    color: #7a7a7a;
    min-width: 2rem;
    text-align: center;
  }

  &__info {
    flex: 1;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__meta {
    display: flex;
    gap: 1.25rem;
    margin-top: 0.35rem;
    font-size: 12px;
    color: #7a7a7a;

    span {
      display: flex;
      align-items: center;
      gap: 0.3rem;
    }
  }

  &__action {
    min-width: 110px;
    text-align: right;
  }
}

.tag-current {
  font-size: 10px;
  font-weight: 600;
  color: #00ace0;
  border: 1px solid #00ace0;
  border-radius: 4px;
  padding: 1px 6px;
}

.btn-revoke {
  font-size: 12px;
  font-weight: 600;
  color: #e03e3e;
  background: transparent;
  border: 1px solid #e03e3e;
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #e03e3e;
    color: #fff;
  }
}

 h3 {
  font-size: 1.1rem !important;
  margin-top: 1rem;
  color: #484c53;
  font-weight: 600;
  padding-left: 0.5rem;
 }
</style>
